import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Grid,
    Divider,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    RadioGroup,
    Radio,
    FormControl,
    FormLabel
} from '@mui/material';
import { adminInstance } from '../../utils/axios';

const defaultSettings = {
    siteName: '',
    siteDescription: '',
    contactEmail: '',
    maintenanceMode: false,
    registrationEnabled: true,
    examCreationEnabled: true,
    paypal: {
        clientId: '',
        clientSecret: '',
        mode: 'sandbox',
        enabled: false
    }
};

const AdminSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [settings, setSettings] = useState(defaultSettings);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordError, setPasswordError] = useState(null);
    const [passwordSuccess, setPasswordSuccess] = useState(null);
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await adminInstance.get('/api/admin/settings');
                setSettings(response.data || defaultSettings);
            } catch (err) {
                setError(err.response?.data?.message || 'Error fetching settings');
                setSettings(defaultSettings);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        
        // Handle nested paypal properties
        if (name.startsWith('paypal.')) {
            const paypalField = name.split('.')[1];
            setSettings(prev => ({
                ...prev,
                paypal: {
                    ...prev.paypal,
                    [paypalField]: e.target.type === 'checkbox' ? checked : value
                }
            }));
        } else {
            setSettings(prev => ({
                ...prev,
                [name]: e.target.type === 'checkbox' ? checked : value
            }));
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await adminInstance.put('/api/admin/settings', settings);
            setSuccess('Settings updated successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Error updating settings');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordSubmit = async () => {
        // Reset states
        setPasswordError(null);
        setPasswordSuccess(null);
        setChangingPassword(true);

        // Validate passwords
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError("New passwords don't match");
            setChangingPassword(false);
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setPasswordError("New password must be at least 6 characters");
            setChangingPassword(false);
            return;
        }

        try {
            // Call API to change password
            await adminInstance.put('/api/admin/password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            
            setPasswordSuccess('Password changed successfully');
            // Reset password fields
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            
            // Close dialog after a short delay
            setTimeout(() => {
                if (passwordSuccess) {
                    setOpenPasswordDialog(false);
                }
            }, 1500);
        } catch (err) {
            setPasswordError(err.response?.data?.message || 'Error changing password');
        } finally {
            setChangingPassword(false);
        }
    };

    const handleOpenPasswordDialog = () => {
        setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordError(null);
        setPasswordSuccess(null);
        setOpenPasswordDialog(true);
    };

    const handleTestPaypalConnection = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            
            const response = await adminInstance.post('/api/admin/settings/test-paypal');
            setSuccess(`PayPal connection successful! Mode: ${response.data.mode}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Error testing PayPal connection');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container>
            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 4 }}>
                Admin Settings
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            General Settings
                        </Typography>
                        
                        <Box sx={{ display: 'block', mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Site Name"
                                name="siteName"
                                value={settings.siteName}
                                onChange={handleChange}
                                required
                            />
                        </Box>

                        <Box sx={{ display: 'block', mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Contact Email"
                                name="contactEmail"
                                type="email"
                                value={settings.contactEmail}
                                onChange={handleChange}
                                required
                            />
                        </Box>

                        <Box sx={{ display: 'block', mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Site Description"
                                name="siteDescription"
                                multiline
                                rows={3}
                                value={settings.siteDescription}
                                onChange={handleChange}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />
                    
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Payment Settings
                        </Typography>
                        
                        <Box sx={{ display: 'block', mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.paypal?.enabled || false}
                                        onChange={handleChange}
                                        name="paypal.enabled"
                                    />
                                }
                                label="Enable PayPal Payments"
                            />
                        </Box>
                        
                        <Box sx={{ display: 'block', mb: 3 }}>
                            <TextField
                                fullWidth
                                label="PayPal Client ID"
                                name="paypal.clientId"
                                value={settings.paypal?.clientId || ''}
                                onChange={handleChange}
                                disabled={!settings.paypal?.enabled}
                            />
                        </Box>
                        
                        <Box sx={{ display: 'block', mb: 3 }}>
                            <TextField
                                fullWidth
                                label="PayPal Client Secret"
                                name="paypal.clientSecret"
                                value={settings.paypal?.clientSecret || ''}
                                onChange={handleChange}
                                disabled={!settings.paypal?.enabled}
                                type="password"
                            />
                        </Box>
                        
                        <Box sx={{ display: 'block', mb: 3 }}>
                            <FormControl component="fieldset" disabled={!settings.paypal?.enabled}>
                                <FormLabel component="legend">PayPal Mode</FormLabel>
                                <RadioGroup
                                    row
                                    name="paypal.mode"
                                    value={settings.paypal?.mode || 'sandbox'}
                                    onChange={handleChange}
                                >
                                    <FormControlLabel value="sandbox" control={<Radio />} label="Sandbox (Testing)" />
                                    <FormControlLabel value="production" control={<Radio />} label="Production (Live)" />
                                </RadioGroup>
                            </FormControl>
                        </Box>
                        
                        <Box sx={{ display: 'block', mb: 3 }}>
                            <Button
                                variant="outlined"
                                color="info"
                                onClick={handleTestPaypalConnection}
                                disabled={!settings.paypal?.enabled || !settings.paypal?.clientId || !settings.paypal?.clientSecret || saving}
                            >
                                Test PayPal Connection
                            </Button>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />
                    
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            System Settings
                        </Typography>

                        <Box sx={{ display: 'block', mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.maintenanceMode}
                                        onChange={handleChange}
                                        name="maintenanceMode"
                                    />
                                }
                                label="Maintenance Mode"
                            />
                        </Box>

                        <Box sx={{ display: 'block', mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.registrationEnabled}
                                        onChange={handleChange}
                                        name="registrationEnabled"
                                    />
                                }
                                label="Enable User Registration"
                            />
                        </Box>

                        <Box sx={{ display: 'block', mb: 3 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.examCreationEnabled}
                                        onChange={handleChange}
                                        name="examCreationEnabled"
                                    />
                                }
                                label="Enable Exam Creation"
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleOpenPasswordDialog}
                        >
                            Change Admin Password
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Settings'}
                        </Button>
                    </Box>
                </form>
            </Paper>

            {/* Password Change Dialog */}
            <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)}>
                <DialogTitle>Change Admin Password</DialogTitle>
                <DialogContent>
                    {passwordError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {passwordError}
                        </Alert>
                    )}
                    {passwordSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {passwordSuccess}
                        </Alert>
                    )}
                    <Box sx={{ display: 'block', mb: 3, pt: 1 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            name="currentPassword"
                            label="Current Password"
                            type="password"
                            fullWidth
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            variant="outlined"
                        />
                    </Box>
                    <Box sx={{ display: 'block', mb: 3 }}>
                        <TextField
                            margin="dense"
                            name="newPassword"
                            label="New Password"
                            type="password"
                            fullWidth
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            variant="outlined"
                        />
                    </Box>
                    <Box sx={{ display: 'block', mb: 1 }}>
                        <TextField
                            margin="dense"
                            name="confirmPassword"
                            label="Confirm New Password"
                            type="password"
                            fullWidth
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            variant="outlined"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPasswordDialog(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handlePasswordSubmit} 
                        color="primary"
                        variant="contained"
                        disabled={changingPassword}
                    >
                        {changingPassword ? 'Changing...' : 'Change Password'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminSettings; 